import React, { useState, useEffect } from 'react';
import { object, string, bool } from 'prop-types';
import Cookies from 'js-cookie';

import Slide from '@material-ui/core/Slide';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { styled } from '@material-ui/core';

import Button from '../../../../Button/Button';
import Image from '../../../../Image/Image';
import Typography from '../../../../Typography/Typography';
import ROUTES, { getPath } from '../../../../../routes';
import { makeURLWithParams } from '../../../../../utils/globals';

import useConnect from './hooks';
import messages from './messages';

export const COOKIE_NAME = 'CookieAgreement';

export const COOKIE_VALUE = 'drsa';

const getCookie = name => Boolean(Cookies.get(name));

const CookieButton = styled(ButtonBase)(({ theme }) => ({
  position: 'fixed',
  left: '0px',
  bottom: '20%',
  transform: 'translateX(-50%)',
  zIndex: theme.zIndex.snackbar,
  maxWidth: '48px',
  borderRadius: '50%',
  transitionDuration: '0.5s',
  boxShadow: theme.shadows[4],
  '&:hover': {
    transform: 'translateX(0)',
  },
}));

const BannerPaper = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: '0px',
  zIndex: theme.zIndex.snackbar,
  width: '100vw',
  backgroundColor: '#eee',
  padding: '16px 8px',
  '& b': {
    display: 'inline-block',
  },
}));

const MoreButton = styled(Button)({
  flex: '0 1 49%',
  backgroundColor: '#F5F5F5',
  color: '#6F7072',
  '&:hover': {
    backgroundColor: '#eee',
  },
});

const NewCookieBanner = ({ settings: { COOKIES_PDF, COOKIE_DURATION }, isEmbedded, token }) => {
  const { setOptionalCookies } = useConnect();
  const [isMandatoryCookiesAccepted, setIsMandatoryCookiesAccepted] = useState(getCookie(`${token}_${COOKIE_NAME}`));
  const [isBannerDisplayed, setIsBannerDisplayed] = useState(!isMandatoryCookiesAccepted);
  const [isAnalyticsEnabled, setIsAnalyticsDisabled] = useState(Boolean(localStorage.getItem(`${token}-analytics`)));

  const crossSiteCookie = isEmbedded && { SameSite: 'None', Secure: true };

  const handleBanner = () => {
    setIsBannerDisplayed(!isBannerDisplayed);
  };

  const handleMandatoryCookies = () => {
    Cookies.set(`${token}_${COOKIE_NAME}`, COOKIE_VALUE, { expires: COOKIE_DURATION, ...crossSiteCookie });
    setIsMandatoryCookiesAccepted(getCookie(`${token}_${COOKIE_NAME}`));
  };

  const handleAnalytics = () => {
    setIsAnalyticsDisabled(!isAnalyticsEnabled);
  };

  const onContinue = () => {
    handleMandatoryCookies();
    if (isAnalyticsEnabled) {
      localStorage.setItem(`${token}-analytics`, true);
    }

    if (!isAnalyticsEnabled) {
      localStorage.removeItem(`${token}-analytics`);
    }
    handleBanner();
  };

  useEffect(() => {
    setOptionalCookies({ analytics: true });
  }, []); // eslint-disable-line

  return (
    <>
      <CookieButton onClick={handleBanner}>
        <Image name="cookiebutton" />
      </CookieButton>

      <Slide direction="up" in={isBannerDisplayed} mountOnEnter unmountOnExit>
        <BannerPaper square elevation={0}>
          <Box className="cookie-banner-content" style={{ maxWidth: '700px', margin: 'auto' }}>
            <Box className="cookie-consent">
              <Typography
                name="title"
                variant="subtitle1"
                align="left"
                content={messages.cookieBannerTitle}
                gutterBottom
              />
              <Typography
                name="sub-title"
                component="b"
                variant="inherit"
                align="left"
                content={messages.mandatoryCookiesTitle}
                gutterBottom
              />
              <Typography
                name="paragraph"
                variant="subtitle2"
                align="left"
                content={messages.mandatoryCookiesParagraph}
                gutterBottom
              />
              <Typography
                name="sub-title"
                component="b"
                variant="inherit"
                align="left"
                content={messages.optionalCookiesTitle}
                gutterBottom
              />
            </Box>
            <Box className="optional-cookies">
              <FormControlLabel
                onChange={handleAnalytics}
                control={<Switch color="primary" checked={isAnalyticsEnabled} />}
                label={<Typography variant="subtitle2" content={messages.analyticsCookies} />}
                labelPlacement="start"
                style={{ width: '100%', margin: '0px', justifyContent: 'space-between' }}
              />
            </Box>
            <Box
              className="action-button-container"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '16px',
              }}
            >
              <MoreButton
                variant="outlined"
                content={messages.more}
                href={makeURLWithParams(getPath(ROUTES.LEGISLATION), {
                  pdf: COOKIES_PDF ? 'cookies_legacy' : 'term_condition',
                })}
                target="_blank"
                className="more-button"
              />
              <Button
                content={messages.continue}
                onClick={onContinue}
                className="continue-button"
                style={{ flex: '0 1 49%' }}
              />
            </Box>
          </Box>
        </BannerPaper>
      </Slide>
    </>
  );
};

NewCookieBanner.propTypes = {
  settings: object,
  token: string,
  isEmbedded: bool,
};

export default NewCookieBanner;
